import { JobsFilterType, TimesheetFilterType } from '@/enum'
import {
  CustomFilterTypeExpression,
  DateTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListOrderingTypeExpression,
  ListPagingTypeExpression,
  ListResponseTypeExpression,
  NewAlgoTypeExpression
} from '@/enum/LinqExpressionType'

import type { Filters, FilterValue } from '@/types/service-types/FilterExpressionGenerator.d'

class FilterExpressionGenerator {
  private filters: Filters

  constructor(filters: Filters) {
    this.filters = filters
  }

  public generateFilterExpression(): string {
    const expressions: string[] = []
    Object.entries(this.filters).forEach(([key, filter]) => {
      if (key !== 'Preference') {
        this.processFilterKey(key, filter, expressions)
      }
    })

    return expressions.join('&')
  }

  private processFilterKey(key: string, filter: FilterValue, expressions: string[]): void {
    if (Array.isArray(filter)) {
      expressions.push(`${key}=${filter.join(',')}`)
    } else if (this.isSpecificKey(key)) {
      expressions.push(`${key}=${encodeURIComponent(filter as any)}`)
    } else if (this.isNestedObjectFilter(key, filter)) {
      const nestedExpression = this.generateNestedExpression(filter as Record<string, any>)
      expressions.push(`${key}=${encodeURIComponent(nestedExpression)}`)
    } else if (key === ListOrderingTypeExpression.Ordering && typeof filter === 'object') {
      const orderingExpression = this.generateOrderingExpression(filter)
      expressions.push(`${key}=${encodeURIComponent(orderingExpression)}`)
    } else if (key === ItemTypeExpression.Filter && typeof filter === 'string') {
      expressions.push(`${key}=${encodeURIComponent(filter)}`)
    } else if (typeof filter === 'number') {
      expressions.push(`${key}=${filter}`)
    }
  }

  private generateNestedExpression(filter: Record<string, any>): string {
    let nestedExpression = ''
    Object.entries(filter).forEach(([subKey, subFilter]) => {
      if (
        [
          JobsFilterType.SCHEDULED_DATE as string,
          JobsFilterType.REQUEST_DATE,
          JobsFilterType.COMPLETION_DATE,
          JobsFilterType.CREATED_AT,
          TimesheetFilterType.DATE
        ].includes(subKey)
      )
        return
      if (nestedExpression) nestedExpression += '&&'

      if (subKey === CustomFilterTypeExpression.Expression) {
        nestedExpression += `${subFilter}`
      } else if (typeof subFilter === 'object' && !Array.isArray(subFilter)) {
        nestedExpression += this.generateComplexExpression(subKey, subFilter)
      } else {
        nestedExpression += this.generateExpression(subKey, '=', subFilter)
      }
    })

    return nestedExpression
  }

  private generateComplexExpression(subKey: string, subFilter: Record<string, any>): string {
    return Object.entries(subFilter)
      .map(([operator, value]) => this.generateExpression(subKey, operator, value))
      .join('')
  }

  private generateOrderingExpression(filter: Record<string, any>): string {
    return Object.entries(filter)
      .map(([orderKey, orderValue]) => `${orderKey} ${orderValue}`)
      .join(',')
  }

  private generateExpression(
    property: string,
    operator: string,
    values: boolean | string | number | (string | number)[]
  ): string {
    const allowedOperators = ['==', '=', '!=', '<', '>', '<=', '>=']
    if (!allowedOperators.includes(operator)) {
      throw new Error(`Unsupported operator: ${operator}`)
    }

    if (typeof values === 'boolean') {
      return `${property}${operator}${values}`
    }

    if (Array.isArray(values)) {
      return values
        .map((value, index) => {
          const formattedValue = typeof value === 'boolean' ? value : `"${value}"`
          return `${index === 0 ? '(' : '||'}${property}${operator}${formattedValue}${index === values.length - 1 ? ')' : ''}`
        })
        .join('')
    }

    return `${property}${operator}"${values}"`
  }

  private isSpecificKey(key: string): boolean {
    return [
      DateTypeExpression.start,
      DateTypeExpression.end,
      ListFilterTypeExpression.SearchText,
      DeleteTypeExpression.SearchText,
      ListResponseTypeExpression.Expand,
      ListResponseTypeExpression.Select,
      ItemTypeExpression.Expand,
      NewAlgoTypeExpression.UseNewAlgo,
      ListPagingTypeExpression.Cookie,
      NewAlgoTypeExpression.DisableFiltering.toLowerCase()
    ].includes(key)
  }

  private isNestedObjectFilter(key: string, filter: FilterValue): boolean {
    return (
      [
        ListFilterTypeExpression.Filter as string,
        DeleteTypeExpression.Expression as string,
        ItemTypeExpression.Filter as string
      ].includes(key) && typeof filter === 'object'
    )
  }
}

export default FilterExpressionGenerator
